import { useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { ORIENTATION } from "@miplatform/constants";
import { Box, useTheme } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";

/**
 * A customizable button group component that displays a collection of buttons with smooth transition animations.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Array} [props.data=[]] - Array of button objects with properties like value, label, icon, iconSize, iconColor
 * @param {boolean} [props.condensed=false] - Whether to use condensed styling with smaller padding and font size
 * @param {string | number | undefined} [props.fontWeight] - Custom font weight for the button text
 * @param {string} [props.selectedBackgroundColor] - Custom background color for the selected button indicator
 * @param {string | undefined} [props.backgroundColor] - Custom background color for the button group container
 * @param {string} [props.selectedFontColor] - Custom font color for the selected button text
 * @param {string} [props.orientation=ORIENTATION.HORIZONTAL] - Layout orientation, either horizontal or vertical
 * @param {Function} props.handleClick - Callback function executed when a button is clicked, receives the clicked item as parameter
 * @param {string|number} props.selected - The value of the currently selected button
 * @param {string|number} [props.containerHeight] - Custom height for the button group container
 * @param {Object | undefined} [props.containerStyle] - Custom style object for the button group container
 *
 * @returns {JSX.Element} A button group with animated selection indicator
 */
export function MiButtonGroup({
  data = [],
  condensed = false,
  disabled = false,
  backgroundColor,
  fontWeight,
  selectedBackgroundColor,
  selectedFontColor,
  orientation = ORIENTATION.HORIZONTAL,
  handleClick,
  selected,
  containerHeight,
  containerStyle,
  ...rest
}) {
  const { classes } = useStyles({
    fontWeight,
    backgroundColor,
    selectedBackgroundColor,
    selectedFontColor,
    orientation,
    condensed,
    containerHeight,
    containerStyle,
  });
  const theme = useTheme();
  const [activeButtonRect, setActiveButtonRect] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const activeButton = containerRef.current?.querySelector(
      `[data-value="${selected}"]`
    );
    if (activeButton) {
      const rect = activeButton.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      setActiveButtonRect({
        left: rect.left - containerRect.left,
        top: rect.top - containerRect.top,
        width:
          orientation === ORIENTATION.VERTICAL
            ? containerRect.width - 8
            : rect.width,
        height: rect.height,
      });
    }
  }, [selected, orientation]);

  return (
    <Box
      ref={containerRef}
      data-testid="button-group"
      className={classes.container}
      sx={{
        flexDirection:
          orientation === ORIENTATION.VERTICAL
            ? ORIENTATION.COLUMN
            : ORIENTATION.ROW,
        ...(disabled && { opacity: 0.6 }),
        ...containerStyle,
      }}
      aria-label="outlined primary button group"
    >
      {activeButtonRect && (
        <Box
          className={classes.backgroundTransition}
          style={{
            left: activeButtonRect.left,
            top: activeButtonRect.top,
            width: activeButtonRect.width,
            height: activeButtonRect.height,
          }}
        />
      )}
      {data.map((item) => (
        <MiButton
          key={item.value}
          title={item.label}
          variant="text"
          size="small"
          onClick={() => handleClick(item)}
          className={`${classes.button} ${
            selected === item.value ? classes.selected : classes.unSelected
          }`}
          data-testid={`button-group-child${
            item["data-testid"] || (item.label ? `-${item.label}` : "")
          }`}
          data-value={item.value}
          color={selected === item.value ? "blue" : "grey"}
          disabled={disabled}
          startIcon={
            item.icon && {
              path: item.icon,
              iconSize: item.iconSize || 0.8,
              iconColor:
                item.iconColor ||
                (selected === item.value
                  ? theme.palette.primary.main
                  : theme.palette.grey.main),
            }
          }
          sx={item.sx}
          {...rest}
        />
      ))}
    </Box>
  );
}

const useStyles = makeStyles()(
  (
    theme,
    {
      fontWeight,
      backgroundColor,
      selectedBackgroundColor,
      selectedFontColor,
      orientation,
      condensed,
      containerHeight,
      containerStyle,
    }
  ) => ({
    container: {
      borderRadius: 4,
      backgroundColor: backgroundColor || theme.palette.grey.lightAccent,
      display: "flex",
      gap: theme.spacing(1),
      padding: theme.spacing(condensed ? 0.75 : 1),
      position: "relative",
      ...(containerHeight && { height: containerHeight }),
      ...containerStyle,
    },
    button: {
      justifyContent: orientation === "vertical" ? "flex-start" : "center",
      "&:hover": {
        backgroundColor: "transparent",
        opacity: 1,
      },
      "&.MuiButton-root:hover": {
        backgroundColor: "transparent",
      },
      fontWeight: fontWeight || theme.fontWeight.bold,
      padding: theme.spacing(condensed ? 0.5 : 1, 1),
      fontSize: theme.fontSize.small,
      ...(containerHeight && {
        height: containerHeight - theme.spacing(condensed ? 0.75 : 1),
      }),
    },
    selected: {
      color: selectedFontColor || theme.palette.common.black,
    },
    unSelected: {
      opacity: 0.8,
    },
    backgroundTransition: {
      position: "absolute",
      backgroundColor: selectedBackgroundColor || theme.palette.common.white,
      borderRadius: 4,
      transition: "all 0.4s ease",
    },
  })
);
